import { getCoursePeriodIdForPeriod } from "../../../../js/PeriodHelpers";
import { getSectionTermFilterInfo } from "../../utils/sectionTermUtils";
import { NO_DEPARTMENT_FILTER_DISPLAY, NO_DEPARTMENT_FILTER_VALUE, isNoDepartmentFilterValue } from "../../utils/departmentFilterUtils";
import { sectionPassesSectionFilters } from "./displayFilters";

export const MAGNETBOARD_EXCEL_ADMIN_ROLE_IDS = ["1", "2", "5"];
export const DEFAULT_MAGNETBOARD_EXCEL_EXPORT_OPTIONS = Object.freeze({
  separateDepartments:false,
  includeEmptyRows:false,
  includeClassrooms:true,
  includeStudentSeatCounts:false,
});

const COMBINED_DEPARTMENT_SHEET_ID = "__COMBINED_DEPARTMENT_SHEET__";

const VIEW_LABELS = {
  teacher: "Teacher",
  course: "Course",
  classroom: "Classroom",
  student: "Student",
};

const normalizeValue = (value) => `${value ?? ""}`;

const getSelectedFilterValues = (filterOptions = {}, filterKey) => {
  const options = Array.isArray(filterOptions?.[filterKey]) ? filterOptions[filterKey] : [];
  return options.filter(option => option.selected).map(option => normalizeValue(option.value));
};

const buildLookup = (items = [], idKey) => (
  (Array.isArray(items) ? items : []).reduce((lookup, item) => {
    if(item?.[idKey] !== null && item?.[idKey] !== undefined) {
      lookup[normalizeValue(item[idKey])] = item;
    }
    return lookup;
  }, {})
);

const getPersonDisplayName = (person = {}) => {
  const lastName = person.name || person.last_name || "";
  const firstName = person.first_name || "";
  if(lastName && firstName) return `${lastName}, ${firstName}`;
  return lastName || firstName || "";
};

const getEntityDisplayName = (entity = {}, currentView) => {
  if(entity.display_name) return entity.display_name;

  const entityInfo = entity.display_info || {};
  if(currentView === "student") {
    return getPersonDisplayName(entityInfo) || `Student ${normalizeValue(entity.data_id)}`.trim();
  }
  if(currentView === "teacher") {
    return getPersonDisplayName(entityInfo) || `Teacher ${normalizeValue(entity.data_id)}`.trim();
  }
  if(currentView === "course") {
    const courseName = entityInfo.name || "";
    const courseCode = entityInfo.course_code || "";
    if(courseName && courseCode) return `${courseName} (${courseCode})`;
    return courseName || courseCode || `Course ${normalizeValue(entity.data_id)}`.trim();
  }
  if(currentView === "classroom") {
    return entityInfo.classroom_name || `Classroom ${normalizeValue(entity.data_id)}`.trim();
  }

  return normalizeValue(entity.data_id);
};

const getSectionRowIds = (section = {}, currentView) => {
  if(currentView === "teacher") {
    return [
      section.teacher_id,
      ...(Array.isArray(section.secondary_teachers) ? section.secondary_teachers : []),
    ];
  }
  if(currentView === "course") return [section.course_id];
  if(currentView === "classroom") return [section.classroom_id];
  if(currentView === "student") return Array.isArray(section.student_list) ? section.student_list : [];

  return [];
};

const getCellIndexKey = (entityId, coursePeriodId) => (
  `${normalizeValue(entityId)}\u0000${normalizeValue(coursePeriodId)}`
);

const buildCellSectionIndex = ({ sections = [], currentView, visibleEntityIds = new Set() }) => {
  const cellSectionIndex = new Map();

  (Array.isArray(sections) ? sections : []).forEach(section => {
    const rowIds = [...new Set(getSectionRowIds(section, currentView).map(normalizeValue).filter(Boolean))]
      .filter(rowId => visibleEntityIds.size === 0 || visibleEntityIds.has(rowId));
    const coursePeriodIds = [...new Set(
      (Array.isArray(section?.course_periods) ? section.course_periods : [])
        .map(normalizeValue)
        .filter(Boolean)
    )];

    rowIds.forEach(rowId => {
      coursePeriodIds.forEach(coursePeriodId => {
        const cellKey = getCellIndexKey(rowId, coursePeriodId);
        const cellSections = cellSectionIndex.get(cellKey) || [];
        cellSections.push(section);
        cellSectionIndex.set(cellKey, cellSections);
      });
    });
  });

  return cellSectionIndex;
};

const sectionMatchesCourseViewFilters = ({
  section,
  coursePeriodId,
  selectedCoursePeriods,
  selectedTerms,
}) => {
  const sectionPeriods = (Array.isArray(section?.course_periods) ? section.course_periods : []).map(normalizeValue);

  if(selectedCoursePeriods.length > 0) {
    const matchesSelectedPeriod = selectedCoursePeriods.some(selectedPeriod => (
      selectedPeriod === "unplaced"
        ? sectionPeriods.length === 0
        : sectionPeriods.includes(selectedPeriod)
    ));
    if(!matchesSelectedPeriod) return false;
  }

  if(coursePeriodId && !sectionPeriods.includes(normalizeValue(coursePeriodId))) return false;

  if(selectedTerms.length > 0) {
    const sectionTermValue = normalizeValue(getSectionTermFilterInfo(section).value);
    if(!selectedTerms.includes(sectionTermValue)) return false;
  }

  return true;
};

const sectionIsVisibleForCurrentDay = ({ section, scheduleType, currentViewType, currentDay }) => {
  if(section?.is_lab !== "1" || scheduleType !== "block" || currentViewType !== "day") return true;

  const uniqueDays = new Set(
    (Array.isArray(section.quarter_days) ? section.quarter_days : [])
      .map(quarterDay => normalizeValue(quarterDay?.day))
      .filter(Boolean)
  );

  return uniqueDays.size === 0 || uniqueDays.has(normalizeValue(currentDay));
};

const getMinimumTerm = (section = {}) => (
  (Array.isArray(section.quarter_days) ? section.quarter_days : []).reduce((minimumTerm, quarterDay) => {
    const quarter = parseInt(quarterDay?.quarter, 10);
    return Number.isFinite(quarter) ? Math.min(minimumTerm, quarter) : minimumTerm;
  }, 999)
);

const getSectionFamiliesForCell = ({
  currentView,
  coursePeriodId,
  rawSections,
  sectionsById,
  subsectionParentById,
  sectionSingletonDoubletonMap,
  filterOptions,
  labsOnlyFiltered,
  nonLabsOnlyFiltered,
  singletonsFiltered,
  doubletonsFiltered,
  hasClassroomFiltered,
  noClassroomFiltered,
  scheduleType,
  currentViewType,
  currentDay,
}) => {
  if(!coursePeriodId) return [];

  const selectedCoursePeriods = getSelectedFilterValues(filterOptions, "periods");
  const selectedTerms = getSelectedFilterValues(filterOptions, "terms");
  const loggedMainSectionIds = new Set();

  return (Array.isArray(rawSections) ? rawSections : []).reduce((results, rawSection) => {
    if(currentView === "course" && !sectionMatchesCourseViewFilters({
      section: rawSection,
      coursePeriodId,
      selectedCoursePeriods,
      selectedTerms,
    })) return results;

    const rawSectionId = normalizeValue(rawSection.section_id);
    const mainSectionId = subsectionParentById[rawSectionId] || rawSectionId;
    if(!mainSectionId || loggedMainSectionIds.has(mainSectionId)) return results;

    const mainSection = sectionsById[mainSectionId] || rawSection;
    if(currentView !== "student" && !sectionPassesSectionFilters({
      section: mainSection,
      sectionSingletonDoubletonMap,
      labsOnlyFiltered,
      nonLabsOnlyFiltered,
      singletonsFiltered,
      doubletonsFiltered,
      hasClassroomFiltered,
      noClassroomFiltered,
    })) return results;

    if(!sectionIsVisibleForCurrentDay({
      section: mainSection,
      scheduleType,
      currentViewType,
      currentDay,
    })) return results;

    loggedMainSectionIds.add(mainSectionId);
    results.push(mainSection);
    return results;
  }, []).sort((sectionA, sectionB) => {
    const termDifference = getMinimumTerm(sectionA) - getMinimumTerm(sectionB);
    if(termDifference !== 0) return termDifference;
    return normalizeValue(sectionA.section_number || sectionA.section_id)
      .localeCompare(normalizeValue(sectionB.section_number || sectionB.section_id), undefined, {numeric:true, sensitivity:"base"});
  });
};

const getCourseDisplayName = (section = {}, coursesById = {}) => {
  if(normalizeValue(section.section_type) === "2") return "Prep";
  if(normalizeValue(section.section_type) === "3") return "Duty";

  const courseInfo = coursesById[normalizeValue(section.course_id)] || {};
  const courseName = courseInfo.name || section.course_name || "";
  const courseCode = courseInfo.course_code || section.course_code || "";
  if(courseName && courseCode) return `${courseName} (${courseCode})`;
  if(courseName || courseCode) return courseName || courseCode;
  return section.course_id ? `Course ID ${section.course_id}` : "No course";
};

const getTeacherDisplayNames = (section = {}, teachersById = {}) => {
  const teacherIds = [
    section.teacher_id,
    ...(Array.isArray(section.secondary_teachers) ? section.secondary_teachers : []),
  ].filter(Boolean).map(normalizeValue);

  const loggedTeacherIds = new Set();
  const teacherNames = teacherIds.reduce((results, teacherId) => {
    if(loggedTeacherIds.has(teacherId)) return results;
    loggedTeacherIds.add(teacherId);

    const teacherInfo = teachersById[teacherId];
    results.push(teacherInfo ? (getPersonDisplayName(teacherInfo) || `Teacher ID ${teacherId}`) : `Teacher ID ${teacherId}`);
    return results;
  }, []);

  return teacherNames.length > 0 ? teacherNames.join("; ") : "No teacher";
};

const getClassroomDisplayName = (section = {}, classroomsById = {}) => {
  const classroomId = normalizeValue(section.classroom_id);
  if(!classroomId) return "No classroom";
  return classroomsById[classroomId]?.classroom_name || `Classroom ID ${classroomId}`;
};

const getSectionStudentSeatCount = (section = {}) => (
  new Set((Array.isArray(section.student_list) ? section.student_list : []).map(normalizeValue).filter(Boolean)).size
);

const getLabDaysDisplay = (section = {}) => {
  const labDays = [...new Set(
    (Array.isArray(section.quarter_days) ? section.quarter_days : [])
      .map(quarterDay => normalizeValue(quarterDay?.day))
      .filter(Boolean)
  )].sort((dayA, dayB) => dayA.localeCompare(dayB, undefined, {numeric:true, sensitivity:"base"}));

  return labDays.length > 0 ? labDays.join(", ") : "Not assigned";
};

const formatSectionPart = ({
  section,
  isSubsection,
  coursesById,
  teachersById,
  classroomsById,
  includeClassrooms,
  includeStudentSeatCounts,
}) => {
  const coursePrefix = isSubsection ? "Subsection course" : "Course";
  return [
    ...(normalizeValue(section.is_lab) === "1" ? [
      "LAB SECTION",
      `Days: ${getLabDaysDisplay(section)}`,
    ] : []),
    `${coursePrefix}: ${getCourseDisplayName(section, coursesById)}`,
    `Teachers: ${getTeacherDisplayNames(section, teachersById)}`,
    ...(includeClassrooms ? [`Classroom: ${getClassroomDisplayName(section, classroomsById)}`] : []),
    ...(includeStudentSeatCounts ? [`Student seats: ${getSectionStudentSeatCount(section)}`] : []),
  ].join("\n");
};

const formatSectionFamily = ({
  mainSection,
  sectionsById,
  coursesById,
  teachersById,
  classroomsById,
  includeClassrooms,
  includeStudentSeatCounts,
}) => {
  const sectionParts = [
    {section: mainSection, isSubsection:false},
    ...(Array.isArray(mainSection.subsections) ? mainSection.subsections : [])
      .map(subsectionId => sectionsById[normalizeValue(subsectionId)])
      .filter(Boolean)
      .map(section => ({section, isSubsection:true})),
  ];

  return sectionParts.map(sectionPart => formatSectionPart({
    ...sectionPart,
    coursesById,
    teachersById,
    classroomsById,
    includeClassrooms,
    includeStudentSeatCounts,
  })).join("\n\n");
};

const sanitizeSheetName = (value) => (
  `${value || "Magnetboard"}`.replace(/[\\/?*[\]:]/g, "-").slice(0, 31) || "Magnetboard"
);

const sanitizeFilenamePart = (value) => (
  `${value || "Schedule"}`.replace(/[\\/?%*:|"<>]/g, "-").replace(/\s+/g, " ").trim() || "Schedule"
);

const getNormalizedDepartmentIds = (departmentIds = []) => (
  [...new Set((Array.isArray(departmentIds) ? departmentIds : []).map(normalizeValue).filter(departmentId => (
    departmentId
    && departmentId !== "0"
    && departmentId !== "null"
    && departmentId !== "undefined"
    && !isNoDepartmentFilterValue(departmentId)
  )))]
);

const getSectionFamilyDepartmentIds = ({ mainSection, sectionsById, coursesById, entity }) => {
  const familySections = [
    mainSection,
    ...(Array.isArray(mainSection?.subsections) ? mainSection.subsections : [])
      .map(subsectionId => sectionsById[normalizeValue(subsectionId)])
      .filter(Boolean),
  ];
  const sectionDepartmentIds = getNormalizedDepartmentIds(
    familySections.flatMap(section => section?.departments || [])
  );
  if(sectionDepartmentIds.length > 0) return sectionDepartmentIds;

  const courseDepartmentIds = getNormalizedDepartmentIds(
    familySections.flatMap(section => coursesById[normalizeValue(section?.course_id)]?.departments || [])
  );
  if(courseDepartmentIds.length > 0) return courseDepartmentIds;

  const entityDepartmentIds = getNormalizedDepartmentIds(entity?.display_info?.departments);
  return entityDepartmentIds.length > 0 ? entityDepartmentIds : [NO_DEPARTMENT_FILTER_VALUE];
};

const getUniqueSheetName = (requestedName, usedSheetNames) => {
  const baseName = sanitizeSheetName(requestedName);
  let sheetName = baseName;
  let suffixNumber = 2;

  while(usedSheetNames.has(sheetName.toLowerCase())) {
    const suffix = ` (${suffixNumber})`;
    sheetName = `${baseName.slice(0, 31 - suffix.length)}${suffix}`;
    suffixNumber += 1;
  }
  usedSheetNames.add(sheetName.toLowerCase());
  return sheetName;
};

const getWrappedLineCount = (cellValue, columnWidth) => {
  const availableCharacters = Math.max(1, Number(columnWidth?.wch) || 10);
  return `${cellValue ?? ""}`.split("\n").reduce((lineCount, line) => (
    lineCount + Math.max(1, Math.ceil(line.length / availableCharacters))
  ), 0);
};

export const formatMagnetboardExcelWorksheet = ({ worksheet, rows, columnWidths }) => {
  worksheet["!cols"] = columnWidths;
  worksheet["!rows"] = rows.map((row, rowIndex) => {
    if(rowIndex === 0) return {hpt:22};

    const maxLineCount = row.reduce((maximumLines, cellValue, columnIndex) => (
      Math.max(maximumLines, getWrappedLineCount(cellValue, columnWidths[columnIndex]))
    ), 1);
    return {hpt:Math.min(409, Math.max(20, (maxLineCount * 15) + 4))};
  });

  Object.keys(worksheet).forEach(cellAddress => {
    if(cellAddress.startsWith("!")) return;

    const cell = worksheet[cellAddress];
    const isHeaderCell = /^[A-Z]+1$/.test(cellAddress);
    const isRowNameCell = /^A\d+$/.test(cellAddress) && !isHeaderCell;
    const isLabCell = !isHeaderCell && !isRowNameCell && /(^|\n)LAB SECTION(?:\n|$)/.test(normalizeValue(cell.v));
    cell.s = {
      ...(cell.s || {}),
      ...(isHeaderCell ? {
        fill:{patternType:"solid", fgColor:{rgb:"092745"}},
        font:{bold:true, color:{rgb:"FFFFFF"}},
      } : {}),
      ...(isRowNameCell ? {
        fill:{patternType:"solid", fgColor:{rgb:"E8F0FE"}},
        font:{bold:true, color:{rgb:"092745"}},
        border:{
          top:{style:"thin", color:{rgb:"CBD5E1"}},
          right:{style:"thin", color:{rgb:"CBD5E1"}},
          bottom:{style:"thin", color:{rgb:"CBD5E1"}},
          left:{style:"thin", color:{rgb:"CBD5E1"}},
        },
      } : {}),
      ...(isLabCell ? {
        fill:{patternType:"solid", fgColor:{rgb:"E2F3E8"}},
        font:{color:{rgb:"196B3A"}},
        border:{
          top:{style:"thin", color:{rgb:"8BC7A2"}},
          right:{style:"thin", color:{rgb:"8BC7A2"}},
          bottom:{style:"thin", color:{rgb:"8BC7A2"}},
          left:{style:"thin", color:{rgb:"8BC7A2"}},
        },
      } : {}),
      alignment:{
        ...(cell.s?.alignment || {}),
        vertical:isHeaderCell ? "center" : "top",
        ...(isHeaderCell ? {horizontal:"center"} : {}),
        wrapText:true,
      },
    };
  });

  return worksheet;
};

export const userCanExportMagnetboardExcel = (userInfo) => (
  MAGNETBOARD_EXCEL_ADMIN_ROLE_IDS.includes(normalizeValue(userInfo?.role_id))
);

export const buildMagnetboardExcelExport = ({
  currentSchedule = {},
  currentView,
  currentDay,
  currentViewType,
  periodType,
  scheduleType,
  periodsToShow = [],
  filteredDisplaySchedule = [],
  scheduleStructureData = {},
  sections = [],
  subsections = [],
  courses = [],
  teachers = [],
  classrooms = [],
  departments = [],
  sectionSingletonDoubletonMap = {},
  filterOptions = {},
  labsOnlyFiltered = false,
  nonLabsOnlyFiltered = false,
  singletonsFiltered = false,
  doubletonsFiltered = false,
  hasClassroomFiltered = false,
  noClassroomFiltered = false,
  exportOptions = {},
} = {}) => {
  const normalizedExportOptions = {
    ...DEFAULT_MAGNETBOARD_EXCEL_EXPORT_OPTIONS,
    ...(exportOptions || {}),
  };
  const viewLabel = VIEW_LABELS[currentView] || "Data";
  const sectionsById = buildLookup(sections, "section_id");
  const coursesById = buildLookup(courses, "course_id");
  const teachersById = buildLookup(teachers, "teacher_id");
  const classroomsById = buildLookup(classrooms, "classroom_id");
  const departmentsById = buildLookup(departments, "department_id");
  const subsectionParentById = (Array.isArray(subsections) ? subsections : []).reduce((lookup, subsectionInfo) => {
    if(subsectionInfo?.subsection_id !== null && subsectionInfo?.subsection_id !== undefined) {
      lookup[normalizeValue(subsectionInfo.subsection_id)] = normalizeValue(subsectionInfo.section_id);
    }
    return lookup;
  }, {});
  const periodColumns = (Array.isArray(periodsToShow) ? periodsToShow : []).map(periodInfo => ({
    periodName: periodInfo?.period_name || normalizeValue(periodInfo?.period_id),
    coursePeriodId: getCoursePeriodIdForPeriod(scheduleStructureData, periodInfo?.period_id, {
      period_type: periodType,
      schedule_type: scheduleType,
      current_day: currentDay,
    }),
  }));
  const visibleEntityIds = new Set(
    (Array.isArray(filteredDisplaySchedule) ? filteredDisplaySchedule : [])
      .map(entity => normalizeValue(entity?.data_id))
      .filter(Boolean)
  );
  const cellSectionIndex = buildCellSectionIndex({
    sections,
    currentView,
    visibleEntityIds,
  });

  const selectedDepartmentIds = getSelectedFilterValues(filterOptions, "departments");
  const departmentIsIncluded = departmentId => selectedDepartmentIds.length === 0 || selectedDepartmentIds.some(selectedDepartmentId => (
    isNoDepartmentFilterValue(departmentId)
      ? isNoDepartmentFilterValue(selectedDepartmentId)
      : normalizeValue(selectedDepartmentId) === normalizeValue(departmentId)
  ));
  const departmentRowsById = new Map();

  (Array.isArray(filteredDisplaySchedule) ? filteredDisplaySchedule : []).forEach(entity => {
    const cellsByDepartmentId = new Map();

    periodColumns.forEach((periodColumn, periodIndex) => {
        const sectionFamilies = getSectionFamiliesForCell({
          currentView,
          coursePeriodId: periodColumn.coursePeriodId,
          rawSections:cellSectionIndex.get(getCellIndexKey(entity.data_id, periodColumn.coursePeriodId)) || [],
          sectionsById,
          subsectionParentById,
          sectionSingletonDoubletonMap,
          filterOptions,
          labsOnlyFiltered,
          nonLabsOnlyFiltered,
          singletonsFiltered,
          doubletonsFiltered,
          hasClassroomFiltered,
          noClassroomFiltered,
          scheduleType,
          currentViewType,
          currentDay,
        });

        sectionFamilies.forEach(mainSection => {
          const formattedSection = formatSectionFamily({
            mainSection,
            sectionsById,
            coursesById,
            teachersById,
            classroomsById,
            includeClassrooms:normalizedExportOptions.includeClassrooms,
            includeStudentSeatCounts:normalizedExportOptions.includeStudentSeatCounts,
          });
          const departmentIds = normalizedExportOptions.separateDepartments
            ? getSectionFamilyDepartmentIds({
                mainSection,
                sectionsById,
                coursesById,
                entity,
              }).filter(departmentIsIncluded)
            : [COMBINED_DEPARTMENT_SHEET_ID];

          departmentIds.forEach(departmentId => {
            const departmentCells = cellsByDepartmentId.get(departmentId) || periodColumns.map(() => "");
            departmentCells[periodIndex] = [departmentCells[periodIndex], formattedSection].filter(Boolean).join("\n\n");
            cellsByDepartmentId.set(departmentId, departmentCells);
          });
        });
    });

    if(normalizedExportOptions.includeEmptyRows && cellsByDepartmentId.size === 0) {
      const emptyRowDepartmentIds = normalizedExportOptions.separateDepartments
        ? (getNormalizedDepartmentIds(entity?.display_info?.departments).length > 0
            ? getNormalizedDepartmentIds(entity.display_info.departments)
            : [NO_DEPARTMENT_FILTER_VALUE])
          .filter(departmentIsIncluded)
        : [COMBINED_DEPARTMENT_SHEET_ID];
      emptyRowDepartmentIds.forEach(departmentId => {
        cellsByDepartmentId.set(departmentId, periodColumns.map(() => ""));
      });
    }

    cellsByDepartmentId.forEach((departmentCells, departmentId) => {
      if(!departmentCells.some(Boolean) && !normalizedExportOptions.includeEmptyRows) return;
      const departmentRows = departmentRowsById.get(departmentId) || [];
      departmentRows.push([getEntityDisplayName(entity, currentView), ...departmentCells]);
      departmentRowsById.set(departmentId, departmentRows);
    });
  });

  const scheduleName = sanitizeFilenamePart(currentSchedule?.schedule_name || "Schedule");
  const viewName = `${viewLabel} View`;
  const daySuffix = currentViewType === "day" && currentDay ? ` - Day ${sanitizeFilenamePart(currentDay)}` : "";
  const headerRow = [viewLabel, ...periodColumns.map(periodColumn => periodColumn.periodName)];
  const columnWidths = [
    {wch:28},
    ...periodColumns.map(() => ({wch:46})),
  ];
  const usedSheetNames = new Set();
  const sheets = [...departmentRowsById.entries()]
    .map(([departmentId, departmentRows]) => {
      const isCombinedSheet = departmentId === COMBINED_DEPARTMENT_SHEET_ID;
      const isNoDepartment = isNoDepartmentFilterValue(departmentId);
      const departmentName = isCombinedSheet
        ? viewName
        : isNoDepartment
        ? NO_DEPARTMENT_FILTER_DISPLAY
        : departmentsById[normalizeValue(departmentId)]?.department || `Department ${departmentId}`;
      return {departmentId, departmentName, departmentRows, isCombinedSheet, isNoDepartment};
    })
    .sort((departmentA, departmentB) => {
      if(departmentA.isCombinedSheet !== departmentB.isCombinedSheet) return departmentA.isCombinedSheet ? -1 : 1;
      if(departmentA.isNoDepartment !== departmentB.isNoDepartment) return departmentA.isNoDepartment ? 1 : -1;
      return departmentA.departmentName.localeCompare(departmentB.departmentName, undefined, {numeric:true, sensitivity:"base"});
    })
    .map(departmentInfo => ({
      departmentId:departmentInfo.departmentId,
      departmentName:departmentInfo.departmentName,
      sheetName:getUniqueSheetName(departmentInfo.departmentName, usedSheetNames),
      rows:[headerRow, ...departmentInfo.departmentRows],
      columnWidths,
    }));

  if(sheets.length === 0) {
    sheets.push({
      departmentId:null,
      departmentName:null,
      sheetName:getUniqueSheetName(viewName, usedSheetNames),
      rows:[headerRow],
      columnWidths,
    });
  }

  return {
    sheets,
    filename: `${scheduleName} - ${viewName}${daySuffix}.xlsx`,
  };
};
